import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import { useContentMode } from '@/hooks/useContentMode';

export interface DisplaySettings {
  show_interactive_toasts: boolean;
  show_floating_promo: boolean;
  show_banner_promo: boolean;
  show_template_switcher: boolean;
  show_accessibility_widget: boolean;
  show_promotions_on_events: boolean;
}

const defaultSettings: DisplaySettings = {
  show_interactive_toasts: true,
  show_floating_promo: true,
  show_banner_promo: true,
  show_template_switcher: true,
  show_accessibility_widget: true,
  show_promotions_on_events: true,
};

export const useDisplaySettings = () => {
  const queryClient = useQueryClient();
  const { isHydraMode, isCustomMode } = useContentMode();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['display-settings'],
    queryFn: async (): Promise<DisplaySettings> => {
      const { data, error } = await pmsFrom('site_content')
        .select('show_interactive_toasts, show_floating_promo, show_banner_promo, show_template_switcher, show_accessibility_widget, show_promotions_on_events')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching display settings:', error);
        return defaultSettings;
      }

      return {
        show_interactive_toasts: data?.show_interactive_toasts ?? true,
        show_floating_promo: data?.show_floating_promo ?? true,
        show_banner_promo: data?.show_banner_promo ?? true,
        show_template_switcher: data?.show_template_switcher ?? true,
        show_accessibility_widget: data?.show_accessibility_widget ?? false,
        show_promotions_on_events: data?.show_promotions_on_events ?? true,
      };
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const updateSettings = useMutation({
    mutationFn: async (newSettings: Partial<DisplaySettings>) => {
      // Get existing record
      const { data: existing } = await pmsFrom('site_content')
        .select('id')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existing) {
        const { error } = await pmsFrom('site_content')
          .update(newSettings)
          .eq('id', existing.id);

        if (error) throw error;
      } else {
        // Ensure required columns exist if this is the very first record
        const { error } = await pmsFrom('site_content')
          .insert({
            current_theme: 'glassmorphism',
            current_layout: 'A',
            ...newSettings,
          });

        if (error) throw error;
      }

      return newSettings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['display-settings'] });
    },
  });

  // In Hydra or Custom mode, force template switcher off — create a new object to avoid mutating cache
  const effectiveSettings: DisplaySettings = {
    ...(settings ?? defaultSettings),
    ...((isHydraMode || isCustomMode) ? { show_template_switcher: false } : {}),
  };

  return {
    settings: effectiveSettings,
    isLoading,
    updateSettings,
  };
};
